import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Table, Button, Tag, Modal, message, Dropdown, Menu, Tooltip, Drawer, Input, notification, Card, Statistic, Badge, Spin, Checkbox, Divider } from 'antd';
import { MessageOutlined, DownOutlined, FilePdfOutlined, CommentOutlined, LinkedinOutlined } from '@ant-design/icons';
import RemarksView from 'src/Components/ManagersPanel/applicantsPanel/RemarksView';
import { axiosInstant } from 'src/api';
import RemarksPanel from '../../RemarksPanel';
import { useUser } from 'src/context';
import queryString from 'query-string';
import { ROUTES, StatusType, formatInterviewDateTime } from 'src/Components/common';
import { ChatBadge } from 'src/Components/common/ChatBadge';
import { useChatUnreadCounts } from 'src/Components/common/useChatUnreadCounts';
import ScreeningDetailsModal from '../ScreeningDetails/ScreeningDetailsModal';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const recruiterActionToBackendStatus = {
  shortlist: 'shortlisted_recruiter',
  reject: 'rejected_recruiter',
  submit: 'submitted',
};

const recruiterVisibleStatuses = [
  'pending',
  'shortlisted_recruiter',
  'interviewing',
  'interviewing_recruiter',
  'rejected_recruiter',
  'submitted',
  'under_review',
  // ...add more as needed
];

const recruiterStatusDisplay: { [key: string]: { label: string; color: string } } = {
  pending:                  { label: 'Pending', color: 'default' },
  applied:                  { label: 'Applied', color: 'default' },
  shortlisted_recruiter:    { label: 'Shortlisted by Recruiter', color: 'cyan' },
  shortlisted_by_recruiter: { label: 'Shortlisted by Recruiter', color: 'cyan' },
  interviewing_recruiter:   { label: 'Interview with Recruiter', color: 'blue' },
  interviewing:             { label: 'Interviewing', color: 'blue' },
  rejected_recruiter:       { label: 'Rejected by Recruiter', color: 'red' },
  rejected_by_recruiter:    { label: 'Rejected by Recruiter', color: 'red' },
  submitted:                { label: 'Submitted to Manager', color: 'purple' },
  under_review:             { label: 'Under Review', color: 'processing' },
  rejected_manager:         { label: 'Rejected by Manager', color: 'red' },
  rejected_by_manager:      { label: 'Rejected by Manager', color: 'red' },
  shortlisted_manager:      { label: 'Shortlisted by Manager', color: 'cyan' },
  shortlisted_by_manager:   { label: 'Shortlisted by Manager', color: 'cyan' },
  interviewing_manager:     { label: 'Interview with Manager', color: 'blue' },
  approved_manager:         { label: 'Approved by Manager', color: 'green' },
  approved_by_manager:      { label: 'Approved by Manager', color: 'green' },
  hired:                    { label: 'Hired', color: 'green' },
  withdrawn:                { label: 'Withdrawn', color: 'red' },
  offer_placed:             { label: 'Offer Placed', color: 'orange' },
  offer_accepted:           { label: 'Offer Accepted', color: 'green' },
  offer_rejected:           { label: 'Offer Rejected', color: 'red' },
  offer_revoked:            { label: 'Offer Revoked', color: 'red' },
  offer_expired:            { label: 'Offer Expired', color: 'red' },
  on_hold:                  { label: 'On Hold', color: 'gold' },
};

const getFileUrl = (url?: string) => {
  if (!url) return undefined;
  return url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
};

const JobApplicationsPage: React.FC = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [job, setJob] = useState<any>(null);
  const [applications, setApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [feedbackModal, setFeedbackModal] = useState<{visible: boolean, applicationId?: string}>({visible: false});
  const [feedback, setFeedback] = useState('');
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  const [submitModalVisible, setSubmitModalVisible] = useState(false);
  const [submitNotes, setSubmitNotes] = useState('');
  const [sharedFields, setSharedFields] = useState<string[]>([]);
  const [currentAppId, setCurrentAppId] = useState<string | null>(null);
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
  const [recruiterFeedbackValue, setRecruiterFeedbackValue] = useState('');
  const [feedbackAppId, setFeedbackAppId] = useState<string | null>(null);
  const [currentCandidateFeedback, setCurrentCandidateFeedback] = useState<string>('');
  const [feedbackAppStatus, setFeedbackAppStatus] = useState<string | null>(null);
  const [originalRecruiterFeedback, setOriginalRecruiterFeedback] = useState('');
  const [chatModalVisible, setChatModalVisible] = useState(false);
  const [chatJobApplicationId, setChatJobApplicationId] = useState<string | null>(null);
  const [chatType, setChatType] = useState<"recruiter-candidate" | "recruiter-manager">("recruiter-candidate");
  // Unread messages in the chat with the candidate, per application (the hiring
  // manager chat has its own count on the applicants panel).
  const unreadChatCounts = useChatUnreadCounts('recruiter-candidate');
  const { user } = useUser();
  const [withdrawalReasonModal, setWithdrawalReasonModal] = useState<{ visible: boolean, reason: string }>({ visible: false, reason: "" });
  const [appStats, setAppStats] = useState<{
    pending: number,
    submitted: number,
    shortlisted_recruiter: number,
    rejected_recruiter: number,
    interviewing_recruiter: number,
    offer: number,
    hired: number,
    withdrawn: number,
    other: number,
    total: number
  } | null>(null);
  const [hasInviteUpdates, setHasInviteUpdates] = useState(false);
  const [screeningDetailsModal, setScreeningDetailsModal] = useState<{
    visible: boolean;
    screeningDetails: any;
    candidateName: string;
  }>({ visible: false, screeningDetails: null, candidateName: '' });
  const [screeningInProgress, setScreeningInProgress] = useState<Set<string>>(new Set());
  const screeningTriggeredRef = useRef(false);
  // Removed: const [unreadCount, setUnreadCount] = useState(0); // Now using unreadChatCounts per application

  const smartProfileSections = [
    {
      section: 'Contact Info',
      fields: [
        { label: 'Email address', value: 'email' },
        { label: 'Phone number', value: 'phone' },
        { label: 'LinkedIn profile', value: 'linkedin' },
        { label: 'Location', value: 'location' },
      ],
    },
    {
      section: 'Professional Background',
      fields: [
        { label: 'Current position / title', value: 'positionTitle' },
        { label: 'Years of experience', value: 'yearsOfExperience' },
        { label: 'Resume', value: 'resume' },
      ],
    },
    {
      section: 'Work Preferences',
      fields: [
        { label: 'Job preference (remote/hybrid/on-site)', value: 'jobPreference' },
        { label: 'Open to relocation', value: 'relocation' },
        { label: 'Actively job seeking', value: 'isSearching' },
      ],
    },
    {
      section: 'Application Details',
      fields: [
        { label: 'Pre-screening answers', value: 'preScreeningAnswers' },
        { label: 'AI screening score', value: 'screeningScore' },
      ],
    },
    {
      section: 'Recruiter Input',
      fields: [
        { label: 'Recruiter notes', value: 'recruiterNotes' },
      ],
    },
  ];
  const ALL_FIELDS = smartProfileSections.flatMap(s => s.fields.map(f => f.value));
  const disabledStatuses = [
    'submitted',
    'shortlisted_manager',
    'interviewing_manager',
    'rejected_manager',
    'hired',
    // add any other statuses you want to disable for
  ];
  const fetchAppStats = async () => {
    if (!jobId) return;
    try {
      const res = await axiosInstant.get(`/job-applications/${jobId}/application-stats`);
      setAppStats(res.data);
    } catch {
      setAppStats(null);
    }
  };

  const fetchApplications = async (): Promise<any[]> => {
    if (!jobId) return [];
    setLoading(true);
    try {
      const res = await axiosInstant.get(`/job-applications/applications/by-job/${jobId}`);

      const data = res.data;
      const mapped = (Array.isArray(data) ? data : []).map(app => ({
        ...app,
        firstName: app.profile?.firstName || '',
        lastName: app.profile?.lastName || '',
        candidateEmail: app.profile?.email || '',
        resume: app.profile?.resume || '',
        answers: app.preScreeningAnswers || [],
        _id: app._id,
        candidateFeedback: app.candidateFeedback || '',
        interviewDate: app.interviewDate,
        status: app.status,
        withdrawalReason: typeof app.withdrawalReason === "string" ? app.withdrawalReason : "",
        screeningScore: app.screeningScore,
        screeningDetails: app.screeningDetails,
        screeningDate: app.screeningDate,
        isScreened: app.isScreened || false,
      }));
      setApplications(mapped);

      // Keep the summary cards in step with the table. Every status change already
      // calls fetchApplications, so refreshing stats here means the cards can never
      // be left stale by a caller that forgets to refresh them separately.
      fetchAppStats();

      return mapped;
    } catch {
      message.error('Failed to fetch applications');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const triggerMissingScreenings = async (apps: any[]) => {
    if (screeningTriggeredRef.current) return;

    const unscreened = apps.filter(
      app => app.screeningScore == null && (app.answers?.length > 0)
    );

    if (unscreened.length === 0) return;

    screeningTriggeredRef.current = true;
    const ids: string[] = unscreened.map(app => app._id);
    setScreeningInProgress(new Set(ids));

    await Promise.allSettled(
      ids.map(id => axiosInstant.post(`/job-applications/applications/${id}/screen`))
    );

    setScreeningInProgress(new Set());
    await fetchApplications();
  };

  useEffect(() => {
    if (!jobId) return;
    setLoading(true);
    // Fetch job info
    fetch(`${API_BASE_URL}/jobs/${jobId}`)
      .then(res => res.json())
      .then(data => setJob(data))
      .catch(() => message.error('Failed to fetch job info'));
    fetchApplications().then(apps => triggerMissingScreenings(apps));

    // Check if we need to auto-open a chat from a notification
    const chatToOpen = sessionStorage.getItem('openChatOnLoad');
    if (chatToOpen) {
      try {
        const chatData = JSON.parse(chatToOpen);
        console.log('Auto-opening chat from notification:', chatData);
        
        // Clear the session storage
        sessionStorage.removeItem('openChatOnLoad');
        
        // Open the chat after a short delay to ensure the page is loaded
        setTimeout(() => {
          setChatJobApplicationId(chatData.jobApplicationId);
          setChatType(chatData.chatType || "recruiter-candidate");
          setChatModalVisible(true);
        }, 1000);
      } catch (e) {
        console.error('Error parsing chat data from session storage:', e);
        sessionStorage.removeItem('openChatOnLoad');
      }
    }
  }, [jobId]);

  useEffect(() => {
    fetchAppStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  useEffect(() => {
    if (jobId) {
      axiosInstant.patch(`/job-applications/mark-viewed/${jobId}`);
    }
  }, [jobId]);

  // Listen for openChat events (from notification clicks)
  useEffect(() => {
    const handleOpenChat = (event: any) => {
      const { chatType: notificationChatType, jobApplicationId, candidateId } = event.detail || {};
      console.log('🔔 openChat event received in recruiter dashboard:', { chatType: notificationChatType, jobApplicationId, candidateId });
      
      // For recruiter dashboard, handle both types of chats
      if ((notificationChatType === 'recruiter-candidate' || notificationChatType === 'recruiter-manager') && jobApplicationId) {
        console.log('✅ Opening chat for:', { chatType: notificationChatType, jobApplicationId });
        setChatType(notificationChatType);
        setChatJobApplicationId(jobApplicationId);
        setChatModalVisible(true);
      }
    };

    window.addEventListener('openChat', handleOpenChat);
    
    return () => {
      window.removeEventListener('openChat', handleOpenChat);
    };
  }, []);

  const handleStatusChange = async (applicationId: string, action: keyof typeof recruiterActionToBackendStatus) => {
    const backendStatus = recruiterActionToBackendStatus[action];
    if (!backendStatus) return;
    await axiosInstant.patch(`/job-applications/applications/${applicationId}/status`, { status: backendStatus });
    fetchApplications(); // refetch after update
  };

  const handleMenuClick = (key: string, record: any) => {
    if (key === 'shortlist') handleStatusChange(record._id, 'shortlist');
    if (key === 'reject') handleStatusChange(record._id, 'reject');
    if (key === 'submit') handleStatusChange(record._id, 'submit');
  };

  const handleOpenDrawer = (profile: any) => {
    setSelectedProfile(profile);
    setDrawerVisible(true);
  };

  const handleSchedule = (candidateUserId: string, _jobIdParam: string, jobApplicationId: string, record: any, isReschedule = false) => {
    const candidateName = encodeURIComponent(
      `${record.profile?.firstName || ''} ${record.profile?.lastName || ''}`.trim()
    );
    navigate(
      `/recruiter/interview-calendar?recruiterMode=true&candidateUserId=${candidateUserId}&candidateName=${candidateName}&jobApplicationId=${jobApplicationId}${isReschedule ? '&reschedule=true' : ''}`
    );
  };

  // Statuses where going back to fix the interview time makes no sense - the
  // application is already past this stage or dead.
  const rescheduleBlockedStatuses = [
    'rejected_recruiter', 'rejected_by_recruiter', 'rejected_manager', 'rejected_by_manager',
    'hired', 'withdrawn', 'offer_rejected',
  ];

  const handleWithdraw = (applicationId: string, reason: any) => {
    console.log("Withdrawal reason to show:", reason, typeof reason);
    setWithdrawalReasonModal({ visible: true, reason: typeof reason === "string" ? reason : JSON.stringify(reason) });
  };

  const handleOpenInvitesDrawer = async () => {
    if (!jobId) return;
    try {
      await axiosInstant.post(`/job-applications/${jobId}/mark-invites-as-read`);
      setHasInviteUpdates(false);
      fetchApplications();
    } catch (error) {
      console.error("Error marking invites as read:", error);
      message.error("Failed to mark invites as read");
    }
  };

  const handleOpenRemarks = () => {
    setChatModalVisible(true);
    axiosInstant.post('/remarks/mark-read', { userId: user._id, jobApplicationId: chatJobApplicationId });
    // Note: unread count is now handled per-application in unreadChatCounts
  };

  const columns = [
    { title: 'First Name', dataIndex: 'firstName', key: 'firstName', align: 'center' as const },
    { title: 'Last Name', dataIndex: 'lastName', key: 'lastName', align: 'center' as const },
    {
      title: 'Pre-screening Answers',
      key: 'answers',
      align: 'center' as const,
      render: (_: any, record: any) => (
        <Button 
          onClick={() => Modal.info({
            title: <span style={{ fontSize: '18px', fontWeight: 600 }}>Pre-screening Answers</span>,
            content: (
              <ul style={{ fontSize: '16px' }}>
                {(record.answers || []).map((ans: any, idx: number) => <li key={idx}>{ans.question}: {ans.answer}</li>)}
              </ul>
            ),
          })}
          style={{ fontSize: '16px', height: '40px', padding: '0 16px' }}
        >
          View Answers
        </Button>
      ),
    },
    {
      title: 'Matching %',
      dataIndex: 'screeningScore',
      key: 'screeningScore',
      align: 'center' as const,
      render: (score: number, record: any) => {
        const getScoreColor = (score: number) => {
          if (score >= 80) return '#52c41a'; // Green
          if (score >= 60) return '#faad14'; // Orange
          return '#ff4d4f'; // Red
        };

        if (score !== undefined) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
              <Badge 
                count={`${score}%`} 
                style={{ 
                  backgroundColor: getScoreColor(score),
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '16px'
                }}
              />
              <Button 
                type="link" 
                onClick={() => {
                  setScreeningDetailsModal({
                    visible: true,
                    screeningDetails: record.screeningDetails,
                    candidateName: `${record.firstName} ${record.lastName}`
                  });
                }}
                style={{ padding: 0, height: 'auto', fontSize: '14px' }}
              >
                View Details
              </Button>
            </div>
          );
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
            <Spin size="small" />
            <span style={{ fontSize: '12px', color: '#8c8c8c' }}>Analysing...</span>
          </div>
        );
      },
      width: 160,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center' as const,
      render: (status: string) => {
        const { label, color } = recruiterStatusDisplay[status] || { 
          label: status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown', 
          color: 'default' 
        };
        return (
          <Tag 
            color={color} 
            style={{ 
              fontSize: '14px', 
              padding: '4px 12px',
              borderRadius: '4px',
            }}
          >
            {label}
          </Tag>
        );
      },
    },
    {
      title: '1st Interview',
      key: 'scheduledInterview',
      align: 'center' as const,
      render: (_: any, record: any) => {
        // Debug log
        console.log('1st Interview column - record:', record.firstName, 'status:', record.status, 'interviewDate:', record.interviewDate);
        
        // Show interview date if scheduled (1st interview - recruiter with candidate)
        if (record.interviewDate) {
          const date = new Date(record.interviewDate);
          console.log('Parsed date:', date, 'isValid:', !isNaN(date.getTime()));
          // Check if date is valid
          if (!isNaN(date.getTime())) {
            return (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontSize: '16px', color: 'green', fontWeight: 600 }}>{formatInterviewDateTime(date)}</span>
                {record.interviewLink && (
                  <a
                    href={record.interviewLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '14px', fontWeight: 600 }}
                  >
                    Join interview
                  </a>
                )}
                {!rescheduleBlockedStatuses.includes(record.status) && (
                  <Button
                    size="small"
                    onClick={() => {
                      if (record.profile?.userId && jobId && record._id) {
                        handleSchedule(record.profile.userId, jobId, record._id, record, true);
                      } else {
                        message.error("Missing candidate, job, or application ID");
                      }
                    }}
                    style={{ fontSize: '13px' }}
                  >
                    Reschedule
                  </Button>
                )}
              </div>
            );
          }
        }
        // Show Schedule button only for shortlisted candidates (before 1st interview is scheduled)
        const isShortlisted = record.status === 'shortlisted_recruiter' || record.status === 'shortlisted_by_recruiter';
        if (isShortlisted && !record.interviewDate) {
          return (
            <Button
              type="primary"
              onClick={() => {
                if (record.profile.userId && jobId && record._id) {
                  handleSchedule(record.profile.userId, jobId, record._id, record);
                } else {
                  message.error("Missing candidate, job, or application ID");
                }
              }}
              style={{ fontSize: '16px', height: '40px', padding: '0 16px' }}
            >
              Schedule
            </Button>
          );
        }
        return '-';
      },
    },
    {
      title: "Withdrawal Reason",
      key: "withdrawalReason",
      align: "center" as const,
      render: (reason: string, record: any) =>
        record.status === "withdrawn" && reason ? (
          <Button
            onClick={() => setWithdrawalReasonModal({ visible: true, reason: record.withdrawalReason })}
            style={{ fontSize: '16px', height: '40px', padding: '0 16px' }}
          >
            View Reason
          </Button>
        ) : record.status === "withdrawn" ? "—" : null,
    },
    {
      title: 'Chat',
      key: 'chat',
      align: 'center' as const,
      render: (_: any, record: any) => {
        const unreadCount = unreadChatCounts[record._id] || 0;
        
        return (
          <Tooltip title={`Chat with candidate${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}>
            <ChatBadge count={unreadCount} offset={[-10, 10]}>
              <Button
                icon={<CommentOutlined style={{ color: 'purple', fontSize: 22 }} />}
                onClick={() => {
                  setChatJobApplicationId(record._id);
                  setChatType("recruiter-candidate"); // Default to candidate chat for direct clicks
                  setChatModalVisible(true);
                  axiosInstant.post('/remarks/mark-read', { userId: user._id, jobApplicationId: record._id });
                }}
                style={{ height: '40px', width: '40px' }}
              />
            </ChatBadge>
          </Tooltip>
        );
      },
    },
    {
      title: 'Feedback',
      key: 'feedback',
      align: 'center' as const,
      render: (_: any, record: any) => (
        <Button
          onClick={() => {
            setFeedbackAppId(record._id);
            setRecruiterFeedbackValue(record.feedback ?? '');
            setOriginalRecruiterFeedback(record.feedback ?? '');
            setCurrentCandidateFeedback(record.candidateFeedback ?? '');
            setFeedbackAppStatus(record.status);
            setFeedbackModalVisible(true);
          }}
          style={{ fontSize: '16px', height: '40px', padding: '0 16px' }}
        >
          Feedback
        </Button>
      ),
    },
    {
      title: 'Profile',
      key: 'profile',
      align: 'center' as const,
      render: (_: any, record: any) =>
        record.profile ? (
          <Button 
            onClick={() => handleOpenDrawer(record.profile)}
            style={{ fontSize: '16px', height: '40px', padding: '0 16px' }}
          >
            View Profile
          </Button>
        ) : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center' as const,
      width: 160,
      render: (_: any, record: any) => {
        const isShortlisted = record.status === 'shortlisted_recruiter' || record.status === 'shortlisted_by_recruiter';
        const isInterviewingRecruiter = record.status === 'interviewing_recruiter' || record.status === 'interviewing';
        const isSubmitted = record.status === 'submitted';
        const isRejected = record.status === 'rejected_recruiter' || record.status === 'rejected_by_recruiter';
        const hasInterviewScheduled = !!record.interviewDate;
        
        // Shortlist: disabled if already shortlisted, submitted, rejected, interviewing, or has interview scheduled
        const shortlistDisabled = isShortlisted || isSubmitted || isRejected || isInterviewingRecruiter || hasInterviewScheduled || disabledStatuses.includes(record.status);
        
        // Reject: a candidate can still be rejected after being shortlisted and after
        // the 1st interview is scheduled, so only already-rejected or handed-on
        // (submitted / manager-level) applications are disabled.
        const rejectDisabled = isRejected || isSubmitted || disabledStatuses.includes(record.status);
        
        // Submit: enabled when shortlisted OR when 1st interview is scheduled (interviewing_recruiter), disabled for manager-level statuses
        const canSubmit = isShortlisted || isInterviewingRecruiter;
        const submitDisabled = !canSubmit || disabledStatuses.includes(record.status);
        
        return (
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item
                  key="shortlist"
                  disabled={shortlistDisabled}
                  onClick={() => handleStatusChange(record._id, 'shortlist')}
                  style={{ fontSize: '16px' }}
                >
                  Shortlist
                </Menu.Item>
                <Menu.Item
                  key="reject"
                  disabled={rejectDisabled}
                  onClick={() => handleStatusChange(record._id, 'reject')}
                  style={{ fontSize: '16px' }}
                >
                  Reject
                </Menu.Item>
                <Menu.Item
                  key="submit"
                  disabled={submitDisabled}
                  onClick={() => {
                    setCurrentAppId(record._id);
                    setSharedFields(ALL_FIELDS);
                    setSubmitModalVisible(true);
                  }}
                  style={{ fontSize: '16px' }}
                >
                  Submit to Manager
                </Menu.Item>
              </Menu>
            }
          >
            <Button style={{ fontSize: '16px', height: '40px', padding: '0 16px' }}>
              Actions <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Button
        type="default"
        style={{
          marginBottom: 16,
          marginLeft: 8,
          background: '#fff',
          color: '#222',
          border: '1px solid #d9d9d9',
          fontWeight: 500,
          fontSize: '16px',
          height: '40px'
        }}
        onClick={() => navigate(-1)}
      >
        ← Back
      </Button>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: 32
        }}
      >
        <h2
          style={{
            margin: 0,
            color: "#2f54eb",
            fontWeight: 700,
            fontSize: 36,
            letterSpacing: 1,
            marginBottom: 32,
            textAlign: 'center'
          }}
        >
          Received Applications
        </h2>
      </div>
      {job && (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '8px',
          marginBottom: 24,
          paddingLeft: 8
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ 
              color: '#2f54eb',
              fontWeight: 600,
              fontSize: '22px'
            }}>Company:</span>
            <span style={{ 
              color: '#262626',
              fontSize: '22px'
            }}>{job.companyName}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ 
              color: '#2f54eb',
              fontWeight: 600,
              fontSize: '22px'
            }}>Position Title:</span>
            <span style={{ 
              color: '#262626',
              fontSize: '22px'
            }}>{job.positionTitle}</span>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginBottom: 24 }}>
        {appStats && (
          <>
            <Card bodyStyle={{ textAlign: 'center' }} style={{ minWidth: 120 }}>
              <Statistic title={<span style={{ fontSize: '16px' }}>Pending</span>} value={appStats.pending} valueStyle={{ fontSize: '24px' }} />
            </Card>
            <Card bodyStyle={{ textAlign: 'center' }} style={{ minWidth: 120 }}>
              <Statistic title={<span style={{ fontSize: '16px' }}>Submitted</span>} value={appStats.submitted} valueStyle={{ color: '#2f54eb', fontSize: '24px' }} />
            </Card>
            <Card bodyStyle={{ textAlign: 'center' }} style={{ minWidth: 120 }}>
              <Statistic title={<span style={{ fontSize: '16px' }}>Shortlisted</span>} value={appStats.shortlisted_recruiter} valueStyle={{ color: '#3f8600', fontSize: '24px' }} />
            </Card>
            <Card bodyStyle={{ textAlign: 'center' }} style={{ minWidth: 120 }}>
              <Statistic title={<span style={{ fontSize: '16px' }}>Rejected</span>} value={appStats.rejected_recruiter} valueStyle={{ color: '#cf1322', fontSize: '24px' }} />
            </Card>
            <Card bodyStyle={{ textAlign: 'center' }} style={{ minWidth: 120 }}>
              <Statistic title={<span style={{ fontSize: '16px' }}>Interviewing</span>} value={appStats.interviewing_recruiter} valueStyle={{ color: '#faad14', fontSize: '24px' }} />
            </Card>
            <Card bodyStyle={{ textAlign: 'center' }} style={{ minWidth: 120 }}>
              <Statistic title={<span style={{ fontSize: '16px' }}>Offer</span>} value={appStats.offer} valueStyle={{ color: '#722ed1', fontSize: '24px' }} />
            </Card>
            <Card bodyStyle={{ textAlign: 'center' }} style={{ minWidth: 120 }}>
              <Statistic title={<span style={{ fontSize: '16px' }}>Hired</span>} value={appStats.hired} valueStyle={{ color: '#3f8600', fontSize: '24px' }} />
            </Card>
            <Card bodyStyle={{ textAlign: 'center' }} style={{ minWidth: 120 }}>
              <Statistic title={<span style={{ fontSize: '16px' }}>Withdrawn</span>} value={appStats.withdrawn} valueStyle={{ color: '#cf1322', fontSize: '24px' }} />
            </Card>
            {/* Only appears if a status matches no card, so the cards always reconcile
                with the applicant list rather than silently losing rows. */}
            {appStats.other > 0 && (
              <Card bodyStyle={{ textAlign: 'center' }} style={{ minWidth: 120 }}>
                <Statistic title={<span style={{ fontSize: '16px' }}>Other</span>} value={appStats.other} valueStyle={{ color: '#8c8c8c', fontSize: '24px' }} />
              </Card>
            )}
          </>
        )}
      </div>
      <Table
        columns={columns.map(col => ({
          ...col,
          title: <span style={{ fontSize: '18px', fontWeight: 'bold' }}>{col.title}</span>,
          render: col.render ? ((text, record) => {
            const rendered = (col.render as Function)(text, record);
            if (React.isValidElement(rendered)) {
              return React.cloneElement(rendered as React.ReactElement<any>, {
                style: { ...(rendered as React.ReactElement<any>).props.style, fontSize: '16px' }
              });
            }
            return <span style={{ fontSize: '16px' }}>{String(rendered)}</span>;
          }) : (text) => <span style={{ fontSize: '16px' }}>{String(text)}</span>,
          width: col.width || (col.key === 'actions' ? 200 : col.key === 'answers' ? 180 : 160)
        }))}
        dataSource={applications}
        rowKey="_id"
        loading={loading}
        bordered
        size="middle"
        pagination={{ pageSize: 10, style: { fontSize: '16px' } }}
        style={{ minWidth: 900 }}
        scroll={{ x: true }}
        components={{
          body: {
            cell: (props: any) => (
              <td {...props} style={{ ...props.style, fontSize: '16px' }} />
            ),
          },
        }}
      />
      <Modal
        title="Provide Feedback"
        visible={feedbackModal.visible}
        onOk={() => setFeedbackModal({ visible: false })}
        onCancel={() => setFeedbackModal({ visible: false })}
      >
        <textarea
          value={feedback}
          onChange={e => setFeedback(e.target.value)}
          rows={4}
          style={{ width: '100%' }}
          placeholder="Enter feedback for the candidate"
        />
      </Modal>
      <Modal
        title={<span style={{ fontSize: 17, fontWeight: 700 }}>📋 Submit to Hiring Manager</span>}
        open={submitModalVisible}
        okText="Submit"
        onOk={async () => {
          const hiddenFields = ALL_FIELDS.filter(f => !sharedFields.includes(f));
          await axiosInstant.patch(`/job-applications/applications/${currentAppId}/status`, {
            status: recruiterActionToBackendStatus.submit,
            notes: submitNotes,
            hiddenFields,
          });
          setSubmitModalVisible(false);
          setSubmitNotes('');
          setSharedFields(ALL_FIELDS);
          fetchApplications();
        }}
        onCancel={() => setSubmitModalVisible(false)}
        width={520}
      >
        <div style={{ fontSize: 14 }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Notes for Hiring Manager</div>
            <Input.TextArea
              value={submitNotes}
              onChange={e => setSubmitNotes(e.target.value)}
              rows={3}
              placeholder="Add context or highlights for the hiring manager..."
            />
          </div>

          <Divider style={{ margin: '12px 0' }} />

          <div style={{ fontWeight: 600, marginBottom: 4 }}>Smart Profile — Select what to share</div>
          <div style={{ color: '#888', fontSize: 12, marginBottom: 12 }}>
            All sections are shared by default. Uncheck anything you want to keep private.
          </div>

          {smartProfileSections.map(section => (
            <div key={section.section} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                {section.section}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 0' }}>
                {section.fields.map(field => (
                  <div key={field.value} style={{ width: '50%' }}>
                    <Checkbox
                      checked={sharedFields.includes(field.value)}
                      onChange={e => {
                        if (e.target.checked) setSharedFields(prev => [...prev, field.value]);
                        else setSharedFields(prev => prev.filter(f => f !== field.value));
                      }}
                    >
                      {field.label}
                    </Checkbox>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div style={{ marginTop: 8, color: '#aaa', fontSize: 11 }}>
            To redact content within the resume, upload a redacted version before submitting.
          </div>
        </div>
      </Modal>
      <Drawer
        title={
          <span style={{ fontSize: '20px', fontWeight: 600 }}>
            {selectedProfile?.firstName
              ? `${selectedProfile.firstName}'s Profile`
              : 'Candidate Profile'}
          </span>
        }
        placement="right"
        onClose={() => setDrawerVisible(false)}
        visible={drawerVisible}
        width={480}
      >
        {selectedProfile && (() => {
          const p = selectedProfile;
          const name = `${p.firstName || ''} ${p.lastName || ''}`.trim();
          return (
            <div style={{ fontSize: 15 }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                {p.profilePhoto ? (
                  <img src={getFileUrl(p.profilePhoto)} alt="Profile" style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#1890ff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, fontWeight: 700, flexShrink: 0 }}>
                    {(p.firstName || '?')[0].toUpperCase()}
                  </div>
                )}
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>{name}</div>
                  {p.positionTitle && <div style={{ color: '#666', marginTop: 2 }}>{p.positionTitle}</div>}
                  {p.yearsOfExperience != null && <div style={{ color: '#888', fontSize: 13, marginTop: 2 }}>🏅 {p.yearsOfExperience} yrs experience</div>}
                </div>
              </div>

              {/* Contact */}
              <div style={{ background: '#fafafa', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Contact Info</div>
                {p.email && <div style={{ marginBottom: 4 }}>✉️ {p.email}</div>}
                {p.phoneNumber && <div style={{ marginBottom: 4 }}>📞 {p.phoneNumber}</div>}
                {p.location && <div style={{ marginBottom: 4 }}>📍 {p.location}</div>}
                {p.linkedin && (
                  <a href={p.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>
                    <LinkedinOutlined style={{ marginRight: 4 }} />LinkedIn Profile
                  </a>
                )}
              </div>

              {/* Work Preferences */}
              {(p.jobPreference || p.relocation || p.isSearching != null) && (
                <div style={{ background: '#fafafa', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Work Preferences</div>
                  {p.jobPreference && <div style={{ marginBottom: 4 }}>💼 <b>Preference:</b> {p.jobPreference}</div>}
                  {p.relocation && <div style={{ marginBottom: 4 }}>🏠 <b>Relocation:</b> {p.relocation}</div>}
                  {p.isSearching != null && (
                    <div style={{ marginBottom: 4 }}>🔍 <b>Job status:</b> {p.isSearching ? 'Actively seeking' : 'Open to opportunities'}</div>
                  )}
                </div>
              )}

              {/* Resume */}
              {p.resume && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Resume</div>
                  <Button type="primary" icon={<FilePdfOutlined />} href={getFileUrl(p.resume)} target="_blank" rel="noopener noreferrer" style={{ marginBottom: 10 }}>
                    Open Resume
                  </Button>
                  <iframe src={getFileUrl(p.resume)} title="Resume Preview" width="100%" height="380px" style={{ border: '1px solid #eee', borderRadius: 6, display: 'block' }} />
                </div>
              )}
            </div>
          );
        })()}
      </Drawer>
      <Modal
        title="Feedback"
        visible={feedbackModalVisible}
        onOk={async () => {
          if (recruiterFeedbackValue.trim()) {
            await axiosInstant.patch(`/job-applications/applications/${feedbackAppId}/recruiter-feedback`, {
              feedback: recruiterFeedbackValue,
            });
            notification.success({ message: 'Your feedback has been submitted.' });
          }
          setFeedbackModalVisible(false);
          setRecruiterFeedbackValue('');
          setOriginalRecruiterFeedback('');
          setFeedbackAppId(null);
          setCurrentCandidateFeedback('');
          setFeedbackAppStatus(null);
          fetchApplications();
        }}
        onCancel={() => {
          setFeedbackModalVisible(false);
          setRecruiterFeedbackValue('');
          setOriginalRecruiterFeedback('');
          setFeedbackAppId(null);
          setCurrentCandidateFeedback('');
          setFeedbackAppStatus(null);
        }}
        okText="Submit"
        okButtonProps={{ disabled: !!originalRecruiterFeedback }}
      >
        <div style={{ marginBottom: 16 }}>
          <label><b>Feedback from Candidate:</b></label>
          <div
            style={{
              minHeight: 60,
              background: '#f5f5f5',
              border: '1px solid #eee',
              borderRadius: 4,
              padding: 8,
              marginTop: 4,
              marginBottom: 16,
              color: currentCandidateFeedback ? '#222' : '#888'
            }}
          >
            {currentCandidateFeedback || 'No feedback from candidate yet.'}
          </div>
        </div>
        <div>
          <label><b>Your Feedback for Candidate:</b></label>
          <Input.TextArea
            value={recruiterFeedbackValue}
            onChange={e => setRecruiterFeedbackValue(e.target.value)}
            rows={4}
            placeholder="Enter your feedback"
            disabled={!!originalRecruiterFeedback}
          />
        </div>
      </Modal>
      {chatModalVisible && chatJobApplicationId && (
        <RemarksPanel
          jobApplicationId={chatJobApplicationId}
          type={chatType}
          visible={chatModalVisible}
          onCancel={() => {
            setChatModalVisible(false);
            setChatJobApplicationId(null);
            setChatType("recruiter-candidate"); // Reset to default
          }}
        />
      )}
      <Modal
        title="Withdrawal Reason"
        open={withdrawalReasonModal.visible}
        onCancel={() => setWithdrawalReasonModal({ visible: false, reason: "" })}
        footer={null}
      >
        <div style={{ whiteSpace: "pre-wrap" }}>
          {typeof withdrawalReasonModal.reason === "string"
            ? withdrawalReasonModal.reason
            : JSON.stringify(withdrawalReasonModal.reason)}
        </div>
      </Modal>
      
      <ScreeningDetailsModal
        visible={screeningDetailsModal.visible}
        onClose={() => setScreeningDetailsModal({ visible: false, screeningDetails: null, candidateName: '' })}
        screeningDetails={screeningDetailsModal.screeningDetails}
        candidateName={screeningDetailsModal.candidateName}
      />
    </div>
  );
};

export default JobApplicationsPage;
